import { alert } from './alert'

/**
 * Request location permission and get user's current location.
 * Returns coordinates if permission is granted, null otherwise.
 * On first call the browser may show a native "Allow location?" prompt.
 */
export const requestLocationPermission = async (): Promise<GeolocationPosition | null> => {
  if (!navigator.geolocation) {
    return null
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve(position)
      },
      () => {
        resolve(null)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    )
  })
}

/**
 * Check if geolocation is available in this environment.
 */
export const isGeolocationAvailable = (): boolean => {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator
}

export type LocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

/**
 * Get current location permission state when supported (e.g. Permissions API in browsers).
 * Returns 'unsupported' when the API is not available (e.g. some mobile browsers).
 */
export const getLocationPermissionState = (): Promise<LocationPermissionState> => {
  if (typeof navigator === 'undefined' || !('permissions' in navigator)) {
    return Promise.resolve('unsupported')
  }
  return navigator.permissions
    .query({ name: 'geolocation' as PermissionName })
    .then((result) => result.state as 'granted' | 'denied' | 'prompt')
    .catch(() => 'unsupported' as const)
}

export type LocationPromptResult = 'allowed' | 'denied'

/**
 * Show an in-app prompt before requesting location.
 * On "Allow", call onAllow (e.g. requestLocationPermission + auto-fill).
 */
export const showLocationPrompt = (options: {
  title: string
  message: string
  allowLabel: string
  notNowLabel: string
  onAllow: () => void
  onNotNow?: () => void
}): void => {
  const { title, message, allowLabel, notNowLabel, onAllow, onNotNow } = options
  alert(title, message, [
    { text: notNowLabel, style: 'cancel', onPress: onNotNow },
    { text: allowLabel, onPress: onAllow },
  ])
}
